import { GridView, StackView, Box, Button, Text, Icon } from '@planningcenter/tapestry-react';
import { nanoid } from 'nanoid';
import React, { useState, useRef, useEffect } from 'react';
import { useDropzone } from 'react-dropzone';
import { UploadButton } from 'react-file-utils';
import { useMessageInputContext, useChatContext, useChannelStateContext, AttachmentPreviewList, ChatAutoComplete } from 'stream-chat-react';
import { EmojiPicker } from 'stream-chat-react/emojis';
import { getPrimaryButtonStyleOverrides } from '../../utils/index.js';
import colors from '../../vendor/topbar/colors.js';

const MessageInput = () => {
    var _a, _b;
    const { handleSubmit, isUploadEnabled, maxFilesLeft, numberOfUploads, text, uploadNewFiles, } = useMessageInputContext();
    const { channel } = useChatContext();
    const [focused, setFocused] = useState(false);
    const channelData = (channel === null || channel === undefined ? undefined : channel.data) || {};
    const isFrozen = channelData.frozen;
    const canSendMessage = ((_a = channelData.own_capabilities) === null || _a === undefined ? undefined : _a.includes("send-message")) || false;
    const canReact = ((_b = channelData.own_capabilities) === null || _b === undefined ? undefined : _b.includes("send-reaction")) || false;
    const { multipleUploads } = useChannelStateContext();
    const { getRootProps, isDragActive, isDragReject } = useDropzone({
        disabled: !isUploadEnabled || maxFilesLeft === 0,
        multiple: multipleUploads,
        noClick: true,
        onDrop: uploadNewFiles,
    });
    const { isChildOfModal, ref } = useIsChildOfModal();
    const wrapInputRef = useAutoFocus();
    const disabled = !numberOfUploads && !text.length;
    const borderStyles = isFrozen && canSendMessage
        ? {}
        : {
            borderColor: "separatorTertiary",
            borderTopWidth: isChildOfModal ? 0 : 1,
        };
    if (isFrozen && !canSendMessage) {
        return (React.createElement(InputWrapper, { ...borderStyles },
            React.createElement(MemberFrozenContent, { canReact: canReact })));
    }
    return (React.createElement(React.Fragment, null,
        isFrozen && canSendMessage && (React.createElement(LeaderFrozenBanner, { isChildOfModal: isChildOfModal })),
        React.createElement(InputWrapper, { ...borderStyles },
            React.createElement(GridView, { alignment: "end", columns: "auto 1fr auto", innerRef: ref, spacing: 1 },
                React.createElement(FileUploadUI, { isUploadEnabled: isUploadEnabled, maxFilesLeft: maxFilesLeft, multipleUploads: multipleUploads, uploadNewFiles: uploadNewFiles }),
                React.createElement(StackView, { ...getRootProps({
                        borderColor: "separatorTertiary",
                        borderWidth: 1,
                        className: isChildOfModal ? "is-modal-child" : "",
                        outline: focused ? colors.interaction66 : "none",
                        outlineOffset: 1,
                        outlineStyle: focused ? "solid" : "none",
                        position: "relative",
                        radius: 8,
                        refKey: "innerRef",
                    }) },
                    isDragActive && React.createElement(DropzoneUI, { isDragReject: isDragReject }),
                    isUploadEnabled && !!numberOfUploads && React.createElement(AttachmentPreviewList, null),
                    React.createElement(StackView, { alignment: "end", axis: "horizontal", position: "relative", spacing: 0.5 },
                        React.createElement(Box, { grow: 1, innerRef: wrapInputRef },
                            React.createElement(ChatAutoComplete, { placeholder: "Send a message", onFocus: () => setFocused(true), onBlur: (e) => e.type === "blur" && setFocused(false) })),
                        React.createElement(Box, { marginBottom: 1.25, marginRight: 1 },
                            React.createElement(EmojiPicker, { pickerProps: { previewPosition: "none", theme: "light" } })))),
                React.createElement(Button, { className: "focus-visible", disabled: disabled, icon: {
                        color: "surface",
                        name: "general.upArrow",
                    }, marginBottom: "10px", onClick: handleSubmit, radius: "circle", size: "sm", title: isChildOfModal ? "Save" : "Send", tabIndex: 0, ...getPrimaryButtonStyleOverrides({ disabled }) })))));
};
const FileUploadUI = ({ isUploadEnabled, maxFilesLeft, multipleUploads, uploadNewFiles, }) => {
    const id = React.useMemo(() => nanoid(), []);
    const inputEl = document.getElementById(id);
    const handleKeyDown = (event) => {
        if (inputEl && (event.key === "Enter" || event.key === " ")) {
            event.preventDefault();
            inputEl.click();
        }
    };
    return (React.createElement(Box, { marginBottom: 1.5 },
        React.createElement("label", { htmlFor: id, tabIndex: 0, onKeyDown: handleKeyDown, className: "str-chat__file-label" },
            React.createElement(Icon, { color: colors.lightNeutral45, css: {
                    "&:active": { fill: colors.lightNeutral12 },
                    "&:hover": { fill: colors.lightNeutral24 },
                }, cursor: "pointer", name: "general.paperclip", size: "20px" })),
        React.createElement(UploadButton, { "aria-label": "File upload", className: "str-chat__file-input", disabled: !isUploadEnabled || maxFilesLeft === 0, id: id, multiple: multipleUploads, onFileChange: uploadNewFiles })));
};
const DropzoneUI = ({ isDragReject }) => (React.createElement(StackView, { alignment: "center", backgroundColor: "surfaceTertiary", borderColor: isDragReject ? "error-dark" : "success-darker", borderRadius: 8, borderStyle: "dashed", borderWidth: 1, bottom: 0, distribution: "center", left: 0, position: "absolute", right: 0, top: 0, zIndex: 10 },
    React.createElement(Text, { color: isDragReject ? "error-dark" : "success-darker", size: 4, weight: 600 }, isDragReject
        ? "Some of the files will not be accepted"
        : "Drop your files here")));
const LeaderFrozenBanner = ({ isChildOfModal, }) => (React.createElement(Box, { backgroundColor: "backgroundTertiary", padding: 0.5, borderColor: "separatorTertiary", borderTopWidth: isChildOfModal ? 0 : 1 },
    React.createElement(Text, { size: 5, textAlign: "center", color: "foreground" }, "Replies are frozen for everyone else.")));
const MemberFrozenContent = ({ canReact }) => {
    const message = canReact
        ? "Replies have been disabled by a leader, but you can still add reactions."
        : "Replies and reactions have been disabled by a leader.";
    return (React.createElement(Text, { size: 4, textAlign: "center", color: "foreground", paddingHorizontal: 2 }, message));
};
const InputWrapper = ({ children, ...restProps }) => (React.createElement(StackView, { padding: 2, spacing: 1, ...restProps }, children));
const useIsChildOfModal = () => {
    const [isChildOfModal, setIsChildOfModal] = useState(false);
    const ref = useRef(null);
    useEffect(() => {
        const modal = document.querySelector(".str-chat__modal");
        setIsChildOfModal(modal === null || modal === undefined ? undefined : modal.contains(ref.current));
    }, []);
    return { isChildOfModal, ref };
};
const useAutoFocus = () => {
    const wrapInputRef = React.useRef(null);
    useEffect(() => {
        var _a, _b, _c;
        if (wrapInputRef.current) {
            const input = (_a = wrapInputRef.current) === null || _a === undefined ? undefined : _a.querySelector("textarea");
            input === null || input === undefined ? undefined : input.focus();
            input === null || input === undefined ? undefined : input.setSelectionRange((_b = input === null || input === undefined ? undefined : input.value) === null || _b === undefined ? undefined : _b.length, (_c = input === null || input === undefined ? undefined : input.value) === null || _c === undefined ? undefined : _c.length);
        }
    }, []);
    return wrapInputRef;
};

export { MessageInput };
//# sourceMappingURL=message_input.js.map
